import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';

import http from 'api/http';
import { boatAPI, inspectionAPI, manufacturerAPI, trailerAPI } from 'api/requests';

import WithUser from 'HOC/User';

import NotificationManager from 'dom-managers/notification';

import LoginScene from 'Scenes/LoginScene/index';
import SummaryScene from 'Scenes/SummaryScene';
import NotFoundScene from 'Scenes/NotFoundScene';
import ReportScene from 'Scenes/ReportScene';
import InspectorDashboardScene from 'Scenes/InspectorDashboardScene'
import DashboardScene from 'Scenes/DashboardScene';
import MetricsScene from 'Scenes/Metrics';
import OilManufacturer from 'Scenes/OilManufacturer';
import TrailerManufacturer from 'Scenes/TrailerManufacturer';
import ComponentManufacturer from 'Scenes/ComponentManufacturer';
import BoatManufacturer from 'Scenes/BoatManufacturer';
import Inspection from 'Scenes/Inspection';
import DocumentsList from 'Scenes/DocumentsList';
import AnnualComplianceModal from 'Components/AnnualComplianceModal';
import {
  COMPLIANCE_LEVEL,
  INSPECTION_WARNING_TYPE,
  buildComplianceWarnings,
  buildInspectionWarnings,
  getComplianceRoles,
  getInspectionComplianceRoles,
  getComplianceStages,
  sortComplianceWarnings,
} from '../annualCompliance';

/**
 * Components
 */
import HeaderComponent from './Header';
import Footer from './Footer';
import { ROLES } from 'constants/roles';
import { ROUTE } from 'constants/routing';

@WithUser
class RootRouter extends React.Component {
  state = {
    complianceWarnings: [],
  };

  componentDidMount() {
    const {user, location} = this.props;
    if (!user.token && location.pathname !== ROUTE.LOGIN) {
      user.saveUrlToRedirect(`${location.pathname}${location.hash}${location.search}`);
    }
    this.loadAnnualComplianceWarnings();
    this.scheduleComplianceRefresh();
  }

  componentDidUpdate(prevProps) {
    if (this.getComplianceIdentity(prevProps.user) !== this.getComplianceIdentity(this.props.user)) {
      this.loadAnnualComplianceWarnings();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.complianceRefreshTimer);
    this.complianceRequestId = (this.complianceRequestId || 0) + 1;
  }

  getComplianceIdentity = user => {
    if (!user || !user.token) return '';
    return `${user.token}|${user.getManufacturerCode()}|${(user.roles || []).join(',')}|${user.isInternal()}|${user.isInspector()}`;
  };

  scheduleComplianceRefresh = () => {
    clearTimeout(this.complianceRefreshTimer);
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const isRestricted = this.state.complianceWarnings.some(
      warning => warning.level === COMPLIANCE_LEVEL.RESTRICTED
    );
    const refreshDelay = isRestricted
      ? Math.min(5 * 60 * 1000, tomorrow.getTime() - now.getTime() + 1000)
      : tomorrow.getTime() - now.getTime() + 1000;
    this.complianceRefreshTimer = setTimeout(() => {
      this.loadAnnualComplianceWarnings();
      this.scheduleComplianceRefresh();
    }, refreshDelay);
  };

  loadAnnualComplianceWarnings = () => {
    const { user } = this.props;
    const roles = getComplianceRoles(user);
    const requestId = (this.complianceRequestId || 0) + 1;
    this.complianceRequestId = requestId;

    if (!roles.length) {
      this.setState({ complianceWarnings: [] });
      return;
    }

    const date = new Date();
    const complianceRequests = [];
    roles.forEach(role => {
      getComplianceStages(role, date).forEach(stage => {
        complianceRequests.push(
          http.get(manufacturerAPI.checkList(role, stage.modelYear))
            .then(({ data: checklist }) => buildComplianceWarnings({ role, checklist, date, stage }))
            .catch(() => [])
        );
      });
    });

    getInspectionComplianceRoles(user).forEach(role => {
      const isBoat = role === ROLES.BOAT_MANUFACTURER.serverName;
      const currentModelYearAPI = isBoat ? boatAPI.currentModelYear : trailerAPI.currentModelYear;
      complianceRequests.push(
        Promise.all([
          http.get(currentModelYearAPI),
          http.get(inspectionAPI.inspectionList(isBoat)),
        ])
          .then(([{ data: currentModelYear }, { data: inspections }]) =>
            buildInspectionWarnings({ role, inspections, currentModelYear, date })
          )
          .catch(() => [])
      );
    });

    Promise.all(complianceRequests).then(warningLists => {
      if (requestId !== this.complianceRequestId) return;
      const warnings = sortComplianceWarnings([].concat(...warningLists));
      const restrictedWarnings = warnings.filter(warning => warning.level === COMPLIANCE_LEVEL.RESTRICTED);
      const inspectionWarnings = warnings.filter(warning => warning.itemType === INSPECTION_WARNING_TYPE);
      this.setState(
        { complianceWarnings: restrictedWarnings.length ? restrictedWarnings.concat(inspectionWarnings) : warnings },
        this.scheduleComplianceRefresh
      );
    });
  };

  dismissComplianceWarning = () => {
    this.setState(({ complianceWarnings }) => ({ complianceWarnings: complianceWarnings.slice(1) }));
  };

  _isUserWithoutOrg = () => {
    const {user} = this.props;
    return (user.isInternal() || user.isInspector()) && !user.isManufacturerSelected();
  };

  renderScene = Scene => {
    return ({match}) => {
      const {user, location, history} = this.props;

      let manufacturerCodeFromURL = null;
      const pathname = location.pathname.split('/');
      if (pathname.length > 2) manufacturerCodeFromURL = pathname[1];
      const selectedManufacturerCode = user.getManufacturerCode();

      if (user.token) {
        if (manufacturerCodeFromURL && manufacturerCodeFromURL !== selectedManufacturerCode && Scene !== DashboardScene) {
          http.get(manufacturerAPI.details(manufacturerCodeFromURL))
            .then(({ data }) => user.selectManufacturer(data, true));

          return null;
        } else if (this._isUserWithoutOrg() && Scene !== DashboardScene && Scene !== MetricsScene && Scene !== InspectorDashboardScene) {
          return <Redirect to={user.getDashboardLink()} />;
        } else if (!(user.isInternal() || user.isInspector()) && Scene === DashboardScene) {
          return <Redirect to={user.getSummaryLink()} />;
        } else {
          return <Scene user={user} location={location} history={history} match={match} />;
        }
      } else {
        return <Redirect to={ROUTE.LOGIN} />;
      }
    };
  };

  renderLoginScene = () => {
    const {user, signIn} = this.props;

    if (user.token) {
      if (user.redirect) {
        return <Redirect to={user.redirect} />
      } else if (user.isInternal() || user.isInspector()) {
        return <Redirect to={user.getDashboardLink()} />;
      } else {
        return <Redirect to={user.getSummaryLink()} />;
      }
    }

    return <LoginScene signIn={signIn} user={user} />;
  };

  getQueryStringVariable = (variable) => {
    const { location } = this.props;
    const query = new URLSearchParams(location.search);
    return query.get(variable);
  };

  clearOAuthParamsFromUrl = () => {
    const { location, history } = this.props;
    const cleanPath = location.pathname + location.search.split('?')[0];
    history.replace(cleanPath);
  };

  render() {
    const {user, changeMenu, logOut, location} = this.props;
    const { complianceWarnings } = this.state;
    const complianceWarning = complianceWarnings[0];
    const isComplianceRestricted = !!complianceWarning && complianceWarning.level === COMPLIANCE_LEVEL.RESTRICTED;

    const error = this.getQueryStringVariable('error');
    const errorDescription = this.getQueryStringVariable('error_description');

    if (error) {
      this.clearOAuthParamsFromUrl();
      const message = (errorDescription || error || '').replace(/\+/g, ' ');
      NotificationManager.error(401, message);
      return;
    }

    return (
      <React.Fragment>
        <HeaderComponent
          user={user}
          changeMenu={changeMenu}
          location={location}
          logOut={logOut}
          isComplianceRestricted={isComplianceRestricted}
        />
        <main className={`ect-root ${!user.token ? 'login' : ''}`}>
          <Switch>
            <Route path={ROUTE.DASHBOARD} render={this.renderScene(DashboardScene)} />
            <Route exact path={ROUTE.DOCUMENTS} render={this.renderScene(DocumentsList)} />
            <Route exact path={ROUTE.REPORT} render={this.renderScene(ReportScene)} />
            <Route exact path={ROUTE.INSPECTOR_DASHBOARD} render={this.renderScene(InspectorDashboardScene)} />
            <Route exact path={ROUTE.METRICS} render={this.renderScene(MetricsScene)} />
            <Route exact path={ROUTE.SUMMARY} render={this.renderScene(SummaryScene)} />
            <Route path={ROUTE.INSPECTION} render={this.renderScene(Inspection)} />

            <Route path={ROUTE.BOAT_MANUFACTURER} render={this.renderScene(BoatManufacturer)} />
            <Route path={ROUTE.COMPONENT_MANUFACTURER} render={this.renderScene(ComponentManufacturer)} />
            <Route path={ROUTE.OIL_MANUFACTURER} render={this.renderScene(OilManufacturer)} />
            <Route path={ROUTE.TRAILER_MANUFACTURER} render={this.renderScene(TrailerManufacturer)} />


            <Route exact path={ROUTE.LOGIN} render={this.renderLoginScene} />

            <Route render={this.renderScene(NotFoundScene)} />
          </Switch>
          <Footer />
        </main>
        <AnnualComplianceModal
          warning={complianceWarning}
          warnings={complianceWarnings}
          onDismiss={this.dismissComplianceWarning}
        />
      </React.Fragment>
    );
  }
}

export default RootRouter;



// WEBPACK FOOTER //
// ./src/app/Routing/RootRouter.jsx